import { classifyError } from '@church/data'
import type { ArrangementSwatch } from '@church/domain'
import { switchSection } from '../lib/view-transition.ts'
import { AlertDialog } from '@base-ui/react/alert-dialog'
import { Drawer } from '@base-ui/react/drawer'
import { Field as BaseField } from '@base-ui/react/field'
import { Tabs as BaseTabs } from '@base-ui/react/tabs'
import { faArrowsRotate, faTriangleExclamation, type IconData } from '@church/ui-icons'
import { useIsMutating } from '@tanstack/react-query'
import { createPortal } from 'react-dom'
import { createContext, use, useEffect, useId, useRef, useState } from 'react'
import type {
  ButtonHTMLAttributes,
  ComponentPropsWithRef,
  CSSProperties,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'

/**
 * Web primitives, on Base UI.
 *
 * Base UI supplies behavior and accessibility (focus traps, aria wiring,
 * keyboard interaction); Tailwind tokens from `@church/ui-tokens` supply the
 * look. Mobile has its own layer on gluestack-ui — the two ecosystems are not
 * ready to be one, so what crosses the boundary is *logic*: every screen's
 * data and rules live in `@church/data` and `@church/domain`, and only the
 * markup is written twice (01-ARCHITECTURE.md §2, docs/06).
 *
 * Plain HTML is still the right answer where the platform already does the
 * job — `<button>`, `<input>`, `<select>` — so those stay native.
 */

/**
 * What every pressable thing does when touched: it gives a little (scale),
 * and colours ease rather than snap. Fast, so it reads as the touch itself.
 */
export const pressable =
  'transition-[transform,background-color,color,border-color,opacity] duration-(--duration-fast) ease-out active:scale-97 motion-reduce:transition-none motion-reduce:active:scale-100'

const buttonVariants = {
  primary:
    'bg-brand-solid text-brand-on-solid hover:bg-brand-solid-hover disabled:opacity-50 disabled:cursor-not-allowed',
  secondary:
    'border border-border-default bg-surface-raised text-content-default hover:bg-surface-sunken disabled:opacity-50',
  /**
   * A decision's colour, not the screen's main action (that stays primary):
   * success for saying yes (Accept, Approve), danger for saying no or for
   * what destroys (Reject, Deny, Delete in a confirm).
   */
  success: 'bg-success-solid text-success-on-solid hover:opacity-90 disabled:opacity-50',
  danger: 'bg-danger-solid text-danger-on-solid hover:opacity-90 disabled:opacity-50',
  ghost: 'text-content-muted hover:bg-surface-sunken hover:text-content-default',
  /** A destructive action that is not the point of the screen: Remove in a panel's footer. */
  quietDanger: 'text-danger-solid hover:bg-danger-soft disabled:opacity-50',
} as const

/**
 * An icon (Font Awesome 6 Pro, compiled by `@church/ui-icons`). Decorative
 * unless given a `label`, which makes it an image with that name. Sized by
 * `className` (`size-4` by default); coloured by the text colour.
 */
export function Icon({
  icon,
  label,
  className = 'size-4',
  mono = false,
}: {
  icon: IconData
  label?: string
  className?: string
  /** A duotone icon in one colour: both layers at full strength (a phone's tab bar). */
  mono?: boolean
}) {
  return (
    <svg
      viewBox={icon.viewBox}
      fill="currentColor"
      className={`shrink-0 ${className}`}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
    >
      {icon.paths.map((path) => (
        <path
          key={path.d.slice(0, 24)}
          d={path.d}
          opacity={path.secondary && !mono ? 0.4 : undefined}
        />
      ))}
    </svg>
  )
}

/**
 * A button with an icon. Icons say the common things (add, edit, delete,
 * close, back, search) on their own; a niche or ambiguous action keeps its
 * words beside the icon. Pass `children` for icon + text.
 */
export function Button({
  variant = 'primary',
  icon,
  pending = false,
  pendingLabel,
  className = '',
  children,
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof buttonVariants
  icon?: IconData
  /**
   * The action is under way: a spinner takes the icon's place, the button
   * can't be pressed twice, and (with `pendingLabel`) its words say what is
   * happening — "Saving…", "Sending to 412…".
   */
  pending?: boolean
  pendingLabel?: ReactNode
}) {
  return (
    <button
      type="button"
      disabled={disabled || pending}
      aria-busy={pending || undefined}
      className={`${pressable} inline-flex items-center justify-center gap-2 rounded-control px-button-x py-button-y text-sm font-medium ${buttonVariants[variant]} ${pending ? 'cursor-progress' : ''} ${className}`}
      {...props}
    >
      {pending ? <InlineSpinner /> : icon && <Icon icon={icon} />}
      {pending && pendingLabel ? pendingLabel : children}
    </button>
  )
}

/** A small spinner in the text colour, the size of an icon: inside a button or a row. */
export function InlineSpinner({ className = 'size-4' }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={`inline-block shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent opacity-80 ${className}`}
    />
  )
}

/**
 * An icon-only button. `label` is required: it is the button's accessible
 * name and its tooltip, so an icon is never the only way to know what it does.
 */
export function IconButton({
  icon,
  label,
  variant = 'ghost',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: IconData
  label: string
  variant?: keyof typeof buttonVariants
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`${pressable} inline-flex size-icon-button shrink-0 items-center justify-center rounded-control ${buttonVariants[variant]} ${className}`}
      {...props}
    >
      <Icon icon={icon} />
    </button>
  )
}

/**
 * The ids a `Field` hands to the native control inside it. Base UI's label
 * points at its own `Field.Control`; our controls are native elements, so
 * without this a label sits next to an input that no screen reader connects
 * to it (the Phase 11 accessibility audit found 25 of them).
 */
const FieldIds = createContext<{ id: string; describedBy?: string; invalid: boolean } | null>(null)

function useFieldIds(props: {
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling'
}) {
  const field = use(FieldIds)
  return {
    id: props.id ?? field?.id,
    'aria-describedby': props['aria-describedby'] ?? field?.describedBy,
    'aria-invalid': props['aria-invalid'] ?? (field?.invalid ? true : undefined),
  }
}

// React 19: `ref` is an ordinary prop, so the input can be focused by callers.
export function Input({ className = '', ...props }: ComponentPropsWithRef<'input'>) {
  return (
    <input
      {...useFieldIds(props)}
      className={`w-full rounded-control border border-border-default bg-surface-base px-control-x py-control-y text-sm transition-[border-color,box-shadow] duration-(--duration-fast) text-input placeholder:text-content-subtle focus:border-brand-solid focus:outline-none focus:ring-2 focus:ring-brand-soft ${className}`}
      {...props}
    />
  )
}

export function Textarea({
  className = '',
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...useFieldIds(props)}
      className={`w-full rounded-control border border-border-default bg-surface-base px-control-x py-control-y text-sm transition-[border-color,box-shadow] duration-(--duration-fast) text-input placeholder:text-content-subtle focus:border-brand-solid focus:outline-none focus:ring-2 focus:ring-brand-soft ${className}`}
      {...props}
    />
  )
}

export function Select({ className = '', ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...useFieldIds(props)}
      className={`w-full rounded-control border border-border-default bg-surface-base px-control-x py-control-y text-sm transition-[border-color,box-shadow] duration-(--duration-fast) text-input focus:border-brand-solid focus:outline-none focus:ring-2 focus:ring-brand-soft ${className}`}
      {...props}
    />
  )
}

/**
 * Label + control + validation message.
 *
 * Base UI's Field wires `aria-describedby` and `aria-invalid` between the three
 * for us — a hand-rolled `<label>` wrapper gets the label association right and
 * the error association wrong, which is exactly the part a screen reader needs.
 */
export function Field({
  label,
  error,
  description,
  children,
}: {
  label: string
  error?: string
  description?: string
  children: ReactNode
}) {
  const id = useId()
  const describedBy =
    [description && `${id}-description`, error && `${id}-error`].filter(Boolean).join(' ') ||
    undefined
  return (
    <BaseField.Root className="block space-y-1.5" invalid={!!error}>
      <BaseField.Label htmlFor={id} className="text-sm font-medium text-content-default">
        {label}
      </BaseField.Label>
      <FieldIds value={{ id, describedBy, invalid: !!error }}>{children}</FieldIds>
      {description && (
        <BaseField.Description
          id={`${id}-description`}
          className="block text-xs text-content-muted"
        >
          {description}
        </BaseField.Description>
      )}
      {error && (
        <BaseField.Error
          id={`${id}-error`}
          match={true}
          className="block text-sm text-danger-solid"
        >
          {error}
        </BaseField.Error>
      )}
    </BaseField.Root>
  )
}

/**
 * Destructive actions, behind a real dialog.
 *
 * This replaces `window.confirm`, which blocks the main thread, cannot be
 * styled, cannot be tested, and on a mis-click reads as a browser warning
 * rather than as this app asking. Base UI's AlertDialog traps focus and
 * announces itself, and the confirm button carries the verb — "Delete", not
 * "OK" — because that is what stops the wrong answer at 8:55 on a Sunday.
 */
export function ConfirmButton({
  label,
  title,
  description,
  confirmLabel = 'Confirm',
  confirmVariant = 'danger',
  pendingLabel,
  variant = 'ghost',
  icon,
  iconOnly = false,
  className = '',
  disabled,
  onConfirm,
}: {
  label: string
  /** Shown before the label; with `iconOnly`, instead of it (the label becomes its name). */
  icon?: IconData
  iconOnly?: boolean
  title: string
  description?: ReactNode
  confirmLabel?: string
  /** The confirm button's look: danger for what destroys, primary for what commits. */
  confirmVariant?: 'danger' | 'primary'
  /** While a promise from `onConfirm` runs: "Refunding…". */
  pendingLabel?: string
  variant?: keyof typeof buttonVariants
  className?: string
  disabled?: boolean
  /**
   * The action. Return its promise (`mutateAsync`) and the dialog stays open
   * with the button working until it settles: closed on success, the reason
   * shown in the dialog (with the action still there to retry) on failure.
   */
  onConfirm: () => unknown
}) {
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<unknown>(null)
  // Inside a drawer, the drawer steps aside while this asks (PanelAside).
  const aside = use(PanelAside)

  const confirm = () => {
    setError(null)
    const result = onConfirm()
    if (!(result instanceof Promise)) {
      setOpen(false)
      aside?.confirmed()
      return
    }
    setPending(true)
    result
      .then(() => {
        setOpen(false)
        aside?.confirmed()
      })
      .catch((failure: unknown) => setError(failure))
      .finally(() => setPending(false))
  }

  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (pending) return
        setOpen(next)
        if (next) aside?.ask()
        else {
          setError(null)
          aside?.cancel()
        }
      }}
    >
      <AlertDialog.Trigger
        disabled={disabled}
        aria-label={iconOnly ? label : undefined}
        title={iconOnly ? label : undefined}
        className={`${pressable} inline-flex items-center justify-center gap-2 rounded-control text-sm font-medium ${iconOnly ? 'size-icon-button' : 'px-button-x py-button-y'} ${buttonVariants[variant]} ${className}`}
      >
        {icon && <Icon icon={icon} />}
        {!iconOnly && label}
      </AlertDialog.Trigger>
      <AlertDialog.Portal>
        {/* Above any drawer, the page dimmed well down: this one is a decision. */}
        <AlertDialog.Backdrop
          forceRender
          className="dialog-backdrop fixed inset-0 z-50 bg-black/60"
        />
        <AlertDialog.Viewport className="dialog-inset fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          <AlertDialog.Popup className="dialog-pop w-full max-w-md space-y-3 rounded-card border border-border-subtle bg-surface-overlay p-6 shadow-xl">
            <AlertDialog.Title className="text-base font-semibold text-content-default">
              {title}
            </AlertDialog.Title>
            {description && (
              <AlertDialog.Description className="text-sm text-content-muted">
                {description}
              </AlertDialog.Description>
            )}
            <ErrorNote error={error} />
            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <AlertDialog.Close
                disabled={pending}
                className={`${pressable} inline-flex min-h-11 items-center justify-center rounded-control px-button-x py-button-y text-sm font-medium sm:min-h-0 ${buttonVariants.secondary}`}
              >
                Cancel
              </AlertDialog.Close>
              <Button
                variant={confirmVariant}
                pending={pending}
                pendingLabel={pendingLabel}
                onClick={confirm}
                className="min-h-11 sm:min-h-0"
              >
                {error ? 'Try again' : confirmLabel}
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Viewport>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}

/**
 * A row that scrolls sideways (tabs, pills) fades at the edge that has more
 * past it, so a part cut off reads as "more this way", not as the end. The
 * class goes on the scrolling element, with `tabs-fade`.
 */
export function useEdgeFade() {
  // A callback ref: the row may appear after the screen does (once loaded).
  const [el, ref] = useState<HTMLElement | null>(null)
  const [edges, setEdges] = useState({ start: false, end: false })
  useEffect(() => {
    if (!el) return
    const measure = () =>
      setEdges({
        start: el.scrollLeft > 4,
        end: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      })
    measure()
    el.addEventListener('scroll', measure, { passive: true })
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', measure)
      observer.disconnect()
    }
  }, [el])
  const edge =
    edges.start && edges.end
      ? 'fade-both'
      : edges.end
        ? 'fade-end'
        : edges.start
          ? 'fade-start'
          : ''
  return { ref, edge }
}

/**
 * Tabbed sections, with Base UI's roving focus and arrow-key navigation. The
 * underline slides to the chosen tab; a row wider than the screen scrolls,
 * fades at the edge that has more, and keeps the chosen tab in view.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  children,
}: {
  tabs: readonly T[]
  value: T
  onChange: (value: T) => void
  children: ReactNode
}) {
  // Each Tabs row is its own part of a view transition: it stays put (its
  // underline sliding live) while the content under it changes.
  const transitionName = `tabs-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const list = useRef<HTMLDivElement>(null)
  const fade = useEdgeFade()

  useEffect(() => {
    list.current
      ?.querySelector('[data-active]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  }, [value])

  return (
    <BaseTabs.Root
      value={value}
      onValueChange={(next) => switchSection(() => onChange(next as T))}
      className="space-y-6"
    >
      {/* More tabs than a phone is wide: the row scrolls sideways, edge to edge. */}
      <div
        className="vt-chrome -mx-gutter border-b border-border-subtle sm:mx-0"
        style={{ viewTransitionName: transitionName }}
      >
        <BaseTabs.List
          ref={(el) => {
            list.current = el
            fade.ref(el)
          }}
          className={`tabs-fade relative flex gap-1 overflow-x-auto px-gutter no-scrollbar sm:px-0 ${fade.edge}`}
        >
          {tabs.map((tab) => (
            <BaseTabs.Tab
              key={tab}
              value={tab}
              className="min-h-11 shrink-0 px-3 py-2 text-sm font-medium whitespace-nowrap text-content-muted transition-colors duration-(--duration-fast) hover:text-content-default data-active:text-content-brand sm:px-4"
            >
              {tab}
            </BaseTabs.Tab>
          ))}
          <BaseTabs.Indicator className="tab-indicator absolute bottom-0 left-0 h-0.5 rounded-full bg-brand-solid" />
        </BaseTabs.List>
      </div>
      {children}
    </BaseTabs.Root>
  )
}

/** Whether a media query matches now, following it as the window changes. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const list = window.matchMedia(query)
    const changed = () => setMatches(list.matches)
    changed()
    list.addEventListener('change', changed)
    return () => list.removeEventListener('change', changed)
  }, [query])
  return matches
}

/** Below the desktop layout (lg), where a page's sections become pills. */
export const MOBILE_QUERY = '(max-width: 63.98rem)'

/**
 * A table on a phone: it scrolls sideways inside its card rather than
 * crushing every column to a word a line. Give the table a minimum width
 * (`min-w-xl` and the like) and hide the columns a phone can do without
 * (`hidden sm:table-cell`); this wrapper handles whatever is left over.
 */
export function TableScroll({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={`overflow-x-auto overscroll-x-contain ${className}`}>{children}</div>
}

/**
 * A drawer stepping aside for a question asked from inside it (a
 * ConfirmButton's "Delete this plan?"): it slides away while the dialog asks,
 * comes back on Cancel, and stays away once the answer is yes, unless the
 * screen keeps it open after the work is done (then it returns, showing why).
 */
const PanelAside = createContext<{
  ask: () => void
  cancel: () => void
  confirmed: () => void
} | null>(null)

/** The top-right slot of the drawer around it (null until drawn), for `PanelPrimary`. */
const PanelSlot = createContext<HTMLElement | null | undefined>(undefined)

/**
 * A form's Save, drawn top right in the drawer around it (SidePanel's
 * `primary` slot) while the form keeps its own state and handler. Outside a
 * drawer it stays where it is.
 */
export function PanelPrimary({ children }: { children: ReactNode }) {
  const slot = use(PanelSlot)
  if (slot === undefined) return children
  return slot ? createPortal(children, slot) : null
}

/**
 * Progressive disclosure: a list shows what you need to read at a glance, and
 * choosing a row opens this panel from the right with everything you can
 * change. Destructive actions live in its `footer`, out of the list's way —
 * a Remove beside every row is one slip from the wrong one.
 *
 * Controlled: the screen keeps which row is open. On a phone it rises from
 * the bottom as a sheet and swipes away down.
 */
export function SidePanel({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  primary,
  onBack,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * A step back within the panel (a team's position, back to the team): a
   * chevron before the title. The panel stays open; only what it shows changes.
   */
  onBack?: () => void
  title: ReactNode
  /** Facts about it (whose, when): one small line under the title. */
  description?: ReactNode
  children: ReactNode
  /**
   * Quiet, rarely-needed actions (Delete, Remove from…), in a toolbar along
   * the bottom: one sits on the left, a second on the right.
   */
  footer?: ReactNode
  /**
   * Top right, by the title: the panel's Save (a submit button: give it
   * `form=` the form's id), or, where changes save themselves, whether they
   * have.
   */
  primary?: ReactNode
}) {
  const phone = useMediaQuery(PHONE_QUERY)
  // Mounted already open (a panel shown as its screen appears): open a frame
  // later, so it slides in like any other rather than simply being there.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(frame)
  }, [])
  // Out of the way of a question (PanelAside): asking, or answered and the
  // work still going.
  const [aside, setAside] = useState<'asking' | 'confirmed' | null>(null)
  const working = useIsMutating() > 0
  const [slot, setSlot] = useState<HTMLDivElement | null>(null)
  if (!open && aside !== null) setAside(null)
  useEffect(() => {
    if (aside !== 'confirmed' || working) return
    // Done, and the screen didn't close it: it comes back (with the outcome).
    const timer = window.setTimeout(() => setAside(null), 300)
    return () => window.clearTimeout(timer)
  }, [aside, working])

  return (
    <PanelAside
      value={{
        ask: () => setAside('asking'),
        cancel: () => setAside(null),
        confirmed: () => setAside('confirmed'),
      }}
    >
      <Drawer.Root
        open={open && mounted && aside === null}
        onOpenChange={onOpenChange}
        swipeDirection={phone ? 'down' : 'right'}
      >
        {/* Stepped aside, it stays mounted: the question lives inside it. */}
        <Drawer.Portal keepMounted={aside !== null}>
          <Drawer.Backdrop className="drawer-backdrop fixed inset-0 z-40 bg-black/40" />
          <Drawer.Viewport className={phone ? sheetViewport : panelViewport}>
            <Drawer.Popup className={phone ? sheetPopup : panelPopup} finalFocus={aside === null}>
              <SheetHandle side={phone ? 'top' : 'left'} />
              {/* On a phone the handle closes it (drag, or tap outside); a
                  computer keeps a close button. */}
              <div className="flex items-center gap-3 border-b border-border-default px-panel-x pt-0.5 pb-2.5 sm:pt-3">
                {onBack && (
                  <button
                    type="button"
                    aria-label="Back"
                    title="Back"
                    onClick={onBack}
                    className={`${pressable} -ml-2 -mr-1 inline-flex size-icon-button shrink-0 items-center justify-center rounded-control text-content-muted hover:bg-surface-sunken hover:text-content-default`}
                  >
                    <svg aria-hidden viewBox="0 0 20 20" className="size-5" fill="currentColor">
                      <path d="M12.53 4.47a.75.75 0 0 1 0 1.06L8.06 10l4.47 4.47a.75.75 0 1 1-1.06 1.06l-5-5a.75.75 0 0 1 0-1.06l5-5a.75.75 0 0 1 1.06 0z" />
                    </svg>
                  </button>
                )}
                <div className="min-w-0 flex-1">
                  <Drawer.Title className="truncate text-base leading-snug font-semibold text-content-default">
                    {title}
                  </Drawer.Title>
                  {description && (
                    <Drawer.Description render={<div />} className="text-2xs text-content-muted">
                      <Marquee>{description}</Marquee>
                    </Drawer.Description>
                  )}
                </div>
                <div ref={setSlot} className="flex shrink-0 items-center gap-2 empty:hidden">
                  {primary}
                </div>
                {!phone && (
                  <Drawer.Close
                    aria-label="Close"
                    className={`${pressable} -mr-2 inline-flex size-icon-button shrink-0 items-center justify-center rounded-control text-content-muted hover:bg-surface-sunken hover:text-content-default`}
                  >
                    <svg aria-hidden viewBox="0 0 20 20" className="size-5" fill="currentColor">
                      <path d="m5.28 4.22 4.72 4.72 4.72-4.72 1.06 1.06L11.06 10l4.72 4.72-1.06 1.06L10 11.06l-4.72 4.72-1.06-1.06L8.94 10 4.22 5.28z" />
                    </svg>
                  </Drawer.Close>
                )}
              </div>
              <Drawer.Content className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-panel-x py-panel-y">
                <PanelSlot value={slot}>{children}</PanelSlot>
              </Drawer.Content>
              {footer && (
                <div className="panel-footer flex items-center justify-between gap-2 border-t border-border-default px-2 pt-1.5 sm:pb-1.5">
                  {footer}
                </div>
              )}
            </Drawer.Popup>
          </Drawer.Viewport>
        </Drawer.Portal>
      </Drawer.Root>
    </PanelAside>
  )
}

/**
 * One line that may be longer than its place: it never wraps. Too long, it is
 * feathered at the edges and, after two seconds, drifts to its end and back
 * (or can be swiped); short enough, it simply sits there.
 */
export function Marquee({ children }: { children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null)
  const track = useRef<HTMLSpanElement>(null)
  const [shift, setShift] = useState(0)
  useEffect(() => {
    const outer = box.current
    const inner = track.current
    if (!outer || !inner) return
    const measure = () => setShift(Math.max(0, inner.offsetWidth - outer.clientWidth))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(outer)
    observer.observe(inner)
    return () => observer.disconnect()
  }, [])
  return (
    // Wider than its column by the feather, so the words line up with the title.
    <div ref={box} className="marquee -mx-3 overflow-x-auto no-scrollbar">
      <span
        ref={track}
        data-overflow={shift > 0 || undefined}
        className="marquee-track inline-block px-3 whitespace-nowrap"
        style={
          {
            '--marquee-shift': `-${shift}px`,
            // About 30px a second, with the pauses at either end.
            '--marquee-time': `${Math.max(3, shift / 30 + 1.5)}s`,
          } as CSSProperties
        }
      >
        {children}
      </span>
    </div>
  )
}

/** A phone, where drawers rise from the bottom. */
export const PHONE_QUERY = '(max-width: 39.98rem)'

/*
 * Every drawer has one shape. On a phone: a sheet from the bottom, its top
 * corners rounded, a handle to drag it away. From `sm` up: a panel floating
 * in from the right, rounded, a handle on its left edge to drag it away.
 */
export const sheetViewport = 'fixed inset-0 z-40 flex items-end pt-12'
export const sheetPopup =
  'detail-sheet relative flex max-h-full w-full flex-col rounded-t-sheet border-t border-border-subtle bg-surface-overlay shadow-2xl outline-none'
export const panelViewport = 'panel-inset fixed inset-0 z-40 flex justify-end'
export const panelPopup =
  'side-panel relative flex h-full w-full max-w-md flex-col rounded-card border border-border-subtle bg-surface-overlay shadow-2xl outline-none'

/** The drag handle on a drawer: at the top of a phone's sheet, on the left of a panel. */
export function SheetHandle({ side }: { side: 'top' | 'left' }) {
  return side === 'top' ? (
    <div aria-hidden className="flex shrink-0 justify-center pt-2.5 pb-2">
      <span className="h-1 w-8 rounded-full bg-border-strong" />
    </div>
  ) : (
    <span
      aria-hidden
      className="absolute top-1/2 -left-3 h-12 w-1.5 -translate-y-1/2 rounded-full bg-surface-overlay/80 shadow"
    />
  )
}

/** A labelled group inside a SidePanel. */
export function PanelSection({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: ReactNode
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">{title}</h3>
      {children}
      {hint && <p className="text-xs text-content-muted">{hint}</p>}
    </section>
  )
}

const chipTones = {
  brand: 'bg-brand-soft text-content-brand',
  neutral: 'bg-surface-sunken text-content-muted',
  success: 'bg-success-soft text-success-content',
  warning: 'bg-warning-soft text-warning-content',
  danger: 'bg-danger-soft text-danger-content',
  // Tag colours: never a status (a key's Male / Female / Low…).
  teal: 'bg-teal-soft text-teal-content',
  pink: 'bg-pink-soft text-pink-content',
  violet: 'bg-violet-soft text-violet-content',
} as const

/** A short value worth spotting in a list: a key, a count, a status. */
const chipSizes = {
  sm: 'min-w-7 px-1.5 py-0.5 text-xs',
  md: 'min-w-9 px-2 py-0.5 text-sm',
  lg: 'px-3 py-1.5 text-sm',
} as const

export function Chip({
  tone = 'neutral',
  size = 'sm',
  children,
}: {
  tone?: keyof typeof chipTones
  size?: keyof typeof chipSizes
  children: ReactNode
}) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center gap-1 rounded-chip font-semibold transition-colors duration-(--duration-base) ${chipSizes[size]} ${chipTones[tone]}`}
    >
      {children}
    </span>
  )
}

/** One line of a row's details: what it is, and its value. */
export interface Detail {
  label: string
  value: ReactNode
}

/**
 * A crowded table row on a phone. The table keeps two or three columns below
 * `sm` (hide the rest with `hidden sm:table-cell`); this goes in the first
 * cell, and on a phone the row's summary becomes a button that opens a sheet
 * from the bottom with every detail and the row's actions. From `sm` up the
 * table shows all of it, so this renders `children` and nothing else.
 *
 * `children` is the cell's own content — plain text, not links: it is drawn
 * twice (once as the button). Rows with a page of their own link to it
 * instead; this is for rows that have nowhere else to show their details.
 */
export function DetailSheet({
  title,
  description,
  details,
  actions,
  children,
}: {
  title: string
  description?: string
  details: readonly Detail[]
  /** The row's buttons (Edit, Delete…); full width in the sheet. */
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <>
      <span className="hidden sm:contents">{children}</span>
      <Drawer.Root swipeDirection="down">
        <Drawer.Trigger className="-my-2 flex min-h-10 w-full items-center justify-between gap-2 py-2 text-left sm:hidden">
          <span className="min-w-0">{children}</span>
          <svg
            aria-hidden
            viewBox="0 0 20 20"
            className="size-4 shrink-0 text-content-subtle"
            fill="currentColor"
          >
            <path d="M7.2 4.7 12.5 10l-5.3 5.3-1.06-1.06L10.38 10 6.14 5.76z" />
          </svg>
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Backdrop className="drawer-backdrop fixed inset-0 bg-black/40" />
          <Drawer.Viewport className="fixed inset-0 flex items-end pt-16">
            <Drawer.Popup className="detail-sheet max-h-full w-full overflow-y-auto overscroll-contain rounded-t-sheet border-t border-border-subtle bg-surface-overlay px-panel-x pb-8 shadow-2xl outline-none">
              {/* The title is ruled off from what follows, as in every drawer. */}
              <div className="-mx-panel-x border-b border-border-default px-panel-x pb-3">
                <SheetHandle side="top" />
                <Drawer.Title className="text-base font-semibold text-content-default">
                  {title}
                </Drawer.Title>
                {description && (
                  <Drawer.Description className="mt-1 text-sm text-content-muted">
                    {description}
                  </Drawer.Description>
                )}
              </div>
              <dl className="divide-y divide-border-subtle border-b border-border-subtle">
                {details.map((d) => (
                  <div key={d.label} className="flex items-baseline justify-between gap-4 py-2.5">
                    <dt className="shrink-0 text-sm text-content-muted">{d.label}</dt>
                    <dd className="min-w-0 text-right text-sm break-words text-content-default">
                      {d.value}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 flex flex-col gap-2 *:w-full">
                {actions}
                <Drawer.Close
                  className={`${pressable} inline-flex items-center justify-center rounded-control px-button-x py-2.5 text-sm font-medium ${buttonVariants.secondary}`}
                >
                  Done
                </Drawer.Close>
              </div>
            </Drawer.Popup>
          </Drawer.Viewport>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  )
}

/**
 * A container: a rounded card. A *list* container (rows of people, songs,
 * plan items…) passes `bleed`: on a phone it then runs edge to edge (no side
 * margin, border or rounding) so its rows get the whole width; from `sm` up
 * it is a card like any other. Regular cards — forms, summaries, tiles —
 * never bleed.
 *
 * On a phone a list sits on the page's own surface (from `sm` up it is a
 * raised card like the rest), and neither its rows nor its title
 * are ruled off (`.list-card` in styles.css; on a phone it has no edges at
 * all); a plan's running order passes `lines` to keep its rules and edges,
 * and its teams `lines edgeless`: the rules between their rows, and on a
 * phone no edges either.
 */
export function Card({
  children,
  className = '',
  bleed = false,
  lines = false,
  edgeless = false,
}: {
  children: ReactNode
  className?: string
  /** A list container: edge to edge on a phone. */
  bleed?: boolean
  /** A list that keeps the rules between its rows. */
  lines?: boolean
  /** With `lines`: no rules above and below it on a phone (a plan's teams). */
  edgeless?: boolean
}) {
  return (
    <div
      className={`border-border-subtle ${
        // `overflow-clip`, not `hidden`: it rounds the corners without making
        // the card a scroll box, so a ListTitle inside can still stick.
        bleed
          ? `list-card ${lines ? `list-lines ${edgeless ? '' : 'border-y'}` : ''} -mx-gutter overflow-clip bg-surface-base sm:mx-0 sm:rounded-card sm:border sm:bg-surface-raised`
          : 'rounded-card border bg-surface-raised'
      } ${className}`}
    >
      {children}
    </div>
  )
}

/**
 * The spacing of one row in a list container — the running order's measure:
 * `px-row-x`, `py-row-y` and `min-h-row` (16px, 12px, 48px), so every list in the app reads at one
 * rhythm. Layout (flex, gap, alignment) stays with the row; its primary text
 * is `text-base`, secondary lines `text-sm` muted.
 *
 *   <button className={`${listRow} flex w-full items-center gap-3 text-left`}>
 */
export const listRow = 'min-h-row px-row-x py-row-y'

/** The pastel swatches (`arrangementSwatch`, `weekdaySwatch` in @church/domain) as fills. */
export const swatchClasses: Record<ArrangementSwatch, string> = {
  green: 'bg-swatch-green',
  yellow: 'bg-swatch-yellow',
  orange: 'bg-swatch-orange',
  red: 'bg-swatch-red',
  pink: 'bg-swatch-pink',
  violet: 'bg-swatch-violet',
  blue: 'bg-swatch-blue',
  teal: 'bg-swatch-teal',
}

/**
 * A section header inside a list (a plan's WORSHIP above its songs): a light
 * grey band with a small uppercase label, a step lighter than the list's own
 * ListTitle and set apart from the white rows. `action` sits right.
 */
export function ListHeader({
  children,
  action,
  sticky = false,
}: {
  children: ReactNode
  action?: ReactNode
  /** Stays under the list's ListTitle while its rows scroll by. */
  sticky?: boolean
}) {
  return (
    <div
      className={`list-header flex items-center justify-between gap-2 border-b-2 border-accent-solid bg-surface-section px-4 py-2 ${
        sticky ? 'under-list-title sticky z-1' : ''
      }`}
    >
      <span className="text-xs font-semibold uppercase tracking-wider text-content-muted">
        {children}
      </span>
      {action}
    </div>
  )
}

/**
 * The section a page's pills have chosen (a plan on a phone: Order, Teams…).
 * A ListTitle or SectionTitle that only repeats it hides its words; the pill
 * already says them. Its actions stay.
 */
export const ShownSection = createContext<string | null>(null)

/**
 * A list's own title (Order, Teams, Files), as a music app titles a shelf:
 * bold and larger, on the list's own surface, no icon. It stays under the top bar while its list scrolls by
 * (`--list-top`, which a page with its own sticky bar lowers); a sticky
 * ListHeader sits right under it.
 */
export function ListTitle({
  title,
  small = false,
  children,
}: {
  title: ReactNode
  /**
   * A list a step under its section's SectionTitle (a team under a plan's
   * Teams): its name `text-base` semibold, on a shorter bar on a phone.
   */
  small?: boolean
  /** Actions and figures on the right. */
  children?: ReactNode
}) {
  const repeats = use(ShownSection) === title
  if (repeats && !children) return <h2 className="sr-only">{title}</h2>
  return (
    <div
      className={`list-title sticky z-2 flex items-center justify-between gap-2 border-b border-border-subtle pr-2 pl-4 ${
        small ? 'h-11 sm:h-12' : 'h-14'
      }`}
    >
      <h2
        className={
          repeats
            ? 'sr-only'
            : small
              ? 'min-w-0 truncate text-base leading-6 font-semibold text-content-default'
              : 'min-w-0 truncate text-xl leading-7 font-bold tracking-tight text-content-default'
        }
      >
        {title}
      </h2>
      {children && (
        <div className="list-title-actions ml-auto flex shrink-0 items-center gap-1">
          {children}
        </div>
      )}
    </div>
  )
}

/**
 * A list in its card, under its title. When a page's pills have already
 * chosen it (ShownSection), the title goes and its actions (+, a picker)
 * sit above the card on their own, part of the page rather than the list:
 * no band, no border.
 */
export function ListCard({
  title,
  actions,
  lines = false,
  children,
}: {
  title: string
  /** Actions and figures on the right of the title. */
  actions?: ReactNode
  /** Keeps the rules between its rows and under its title (Card `lines`). */
  lines?: boolean
  children: ReactNode
}) {
  if (use(ShownSection) === title)
    return (
      <section aria-label={title} className="untitled-list space-y-2">
        {/* Pulled toward the edge, so the last button's words line up with the rows'. */}
        {actions && (
          <div className="-mr-3 flex min-h-10 items-center justify-end gap-1">{actions}</div>
        )}
        <Card bleed lines={lines}>
          {children}
        </Card>
      </section>
    )
  return (
    <Card bleed lines={lines}>
      <ListTitle title={title}>{actions}</ListTitle>
      {children}
    </Card>
  )
}

/**
 * A heading over several lists or cards (Teams, above each team's card):
 * the same voice as ListTitle, outside any card, with what is on the right.
 */
export function SectionTitle({ title, children }: { title: ReactNode; children?: ReactNode }) {
  const repeats = use(ShownSection) === title
  if (repeats && !children) return <h2 className="sr-only">{title}</h2>
  return (
    <div className="flex min-h-10 items-center justify-between gap-2">
      <h2
        className={
          repeats
            ? 'sr-only'
            : 'min-w-0 truncate text-xl leading-7 font-bold tracking-tight text-content-default'
        }
      >
        {title}
      </h2>
      {children && (
        <div className="list-title-actions ml-auto flex shrink-0 items-center gap-1">
          {children}
        </div>
      )}
    </div>
  )
}

/**
 * A page's heading. The page's name lives in the top bar's breadcrumbs, so
 * here it is for screen readers only (`visible` shows it — a greeting, or a
 * page outside the shell with no breadcrumbs). What shows is a row of the
 * page's facts (muted, left) and its actions (right) — nothing when it has
 * neither. No taglines describing what the page is for.
 */
export function PageHeader({
  title,
  facts,
  action,
  visible = false,
}: {
  title: string
  /** About the thing shown — "Bible study · Tuesdays 7pm" — never a description of the page. */
  facts?: ReactNode
  action?: ReactNode
  visible?: boolean
}) {
  const heading = (
    <h1 className={visible ? 'text-2xl font-bold text-content-default' : 'sr-only'}>{title}</h1>
  )
  if (!facts && !action) return heading
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div className="min-w-0">
        {heading}
        {facts && <p className="text-sm text-content-muted">{facts}</p>}
      </div>
      {action}
    </div>
  )
}

/**
 * Waiting for something. With no `label`, content is on its way: grey bars
 * in the shape of rows (they appear only if the wait is long enough to
 * notice). With a `label`, a process the person started is running, and the
 * label says which: "Reading songs.csv…", "Connecting Spotify…".
 */
export function Spinner({ label }: { label?: string }) {
  if (!label) return <ListSkeleton />
  return (
    <div className="flex items-center gap-2 text-sm text-content-muted" role="status">
      <InlineSpinner className="size-4 text-content-brand" />
      {label}
    </div>
  )
}

/** Each skeleton row's two bars, varied so the rows read as rows, not stripes. */
const SKELETON_WIDTHS = [
  ['w-3/5', 'w-1/3'],
  ['w-1/2', 'w-2/5'],
  ['w-2/3', 'w-1/4'],
  ['w-1/2', 'w-1/3'],
] as const

/** Rows on their way: bars in the list's own rhythm, fading in after a beat. */
export function ListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div role="status" className="skeleton-delay">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={`${listRow} flex flex-col justify-center gap-2`}>
          <span
            className={`h-3.5 animate-pulse rounded-full bg-surface-sunken ${SKELETON_WIDTHS[i % 4]?.[0]}`}
          />
          <span
            className={`h-3 animate-pulse rounded-full bg-surface-sunken ${SKELETON_WIDTHS[i % 4]?.[1]}`}
          />
        </div>
      ))}
    </div>
  )
}

/**
 * A request that failed, said so a person can act on it: what broke, why,
 * what is still safe (classifyError in @church/data), and — when trying
 * again can work and the screen says how — a Retry right there.
 */
export function ErrorNote({
  error,
  onRetry,
  retrying = false,
  safe,
}: {
  error: unknown
  onRetry?: () => void
  retrying?: boolean
  /** What this screen knows is still safe ("Your plan is unchanged"), instead of the general line. */
  safe?: string
}) {
  if (!error) return null
  const failure = classifyError(error)
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-control border border-danger-solid/30 bg-danger-soft px-3 py-2.5 text-sm text-danger-content"
    >
      <Icon icon={faTriangleExclamation} className="mt-0.5 size-4 text-danger-solid" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{failure.title}</p>
        <p className="mt-0.5">
          {failure.detail} <span className="opacity-80">{safe ?? failure.safe}</span>
        </p>
      </div>
      {onRetry && failure.retryable && (
        <Button
          variant="secondary"
          icon={faArrowsRotate}
          pending={retrying}
          pendingLabel="Trying…"
          onClick={onRetry}
          className="-my-1 shrink-0"
        >
          Retry
        </Button>
      )}
    </div>
  )
}

/**
 * A page or section that could not load: the cause, what is unaffected, and
 * the ways forward (try again; somewhere else to go).
 */
export function ErrorState({
  error,
  onRetry,
  retrying = false,
  safe,
  children,
}: {
  error: unknown
  onRetry?: () => void
  retrying?: boolean
  safe?: string
  /** Other ways forward: a link home, to the list… */
  children?: ReactNode
}) {
  const failure = classifyError(error)
  return (
    <div role="alert" className="mx-auto max-w-md px-6 py-12 text-center">
      <span className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-danger-soft">
        <Icon icon={faTriangleExclamation} className="size-5 text-danger-solid" />
      </span>
      <h2 className="text-lg font-semibold text-content-default">{failure.title}</h2>
      <p className="mt-1 text-sm text-content-muted">{failure.detail}</p>
      <p className="mt-1 text-sm text-content-muted">{safe ?? failure.safe}</p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        {onRetry && failure.retryable && (
          <Button
            icon={faArrowsRotate}
            pending={retrying}
            pendingLabel="Trying again…"
            onClick={onRetry}
          >
            Try again
          </Button>
        )}
        {children}
      </div>
    </div>
  )
}

/** Nothing here yet: what that means, and (`action`) the way to start. */
export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string
  hint?: string
  /** The next step: "+ New list", "Import a CSV". */
  action?: ReactNode
}) {
  return (
    <div className="px-6 py-10 text-center">
      <p className="text-sm font-medium text-content-default">{title}</p>
      {hint && <p className="mt-1 text-sm text-content-muted">{hint}</p>}
      {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  )
}

/**
 * Exactly one of: rows on their way, why they couldn't come (with Try
 * again), nothing yet, or the content. A failed load never reads as "No
 * people yet"; an empty list is only ever one that loaded.
 */
export function QueryState<T>({
  query,
  isEmpty,
  empty,
  rows,
  safe,
  children,
}: {
  query: {
    data: T | undefined
    error: unknown
    isPending: boolean
    isRefetching?: boolean
    refetch: () => unknown
  }
  /** Whether the loaded data has nothing to show. */
  isEmpty?: (data: T) => boolean
  /** What to show when it does (an EmptyState). */
  empty?: ReactNode
  /** How many skeleton rows to show while loading. */
  rows?: number
  /** What is still safe if it fails ("Your lists are safe on the server"). */
  safe?: string
  children: (data: T) => ReactNode
}) {
  if (query.data === undefined) {
    if (query.error)
      return (
        <ErrorState
          error={query.error}
          safe={safe}
          retrying={query.isRefetching}
          onRetry={() => void query.refetch()}
        />
      )
    return <ListSkeleton rows={rows} />
  }
  if (isEmpty?.(query.data) && empty) return <>{empty}</>
  return <>{children(query.data)}</>
}
